import { Link, useParams } from 'react-router-dom';
import { Card } from '../components/Painel';
import { api, type ContentDetailResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Rastreio de um conteúdo: por que existe, quem decidiu, o que cada portão
 * respondeu (`architecture §18`).
 *
 * A tela é longa de propósito. A pergunta que ela responde não é "qual é o
 * estado" — é **"por que esta peça está assim"**, e essa resposta não cabe num
 * resumo. Cada decisão aparece com política, motivo e versão de modelo, porque é
 * isso que permite reconstruir a cadeia meses depois.
 */

const GATE_LABEL: Record<string, string> = {
  editorial: 'Portão editorial',
  preproduction: 'Governança de pré-produção',
  postproduction: 'Governança de pós-produção',
  publication: 'Portão de publicação',
  response: 'Portão de resposta',
  cost: 'Portão de custo',
};

const OUTCOME_LABEL: Record<string, string> = {
  pass: 'Aprovado',
  deny: 'Negado',
  hold: 'Retido',
  revise: 'Revisar',
  escalate: 'Escalado',
};

const OUTCOME_CLASS: Record<string, string> = {
  pass: 'text-ok-ink',
  deny: 'text-danger-ink',
  hold: 'text-warn-ink',
  revise: 'text-warn-ink',
  escalate: 'text-warn-ink',
};

const REVIEWER_LABEL: Record<string, string> = {
  fact: 'Verificação factual',
  brand: 'Marca',
  safety: 'Segurança e constituição',
  voice: 'Voz',
  legal: 'Jurídico',
};

export function ContentDetail() {
  const { id } = useParams<{ id: string }>();
  const resource = useResource<ContentDetailResponse>(() => api.contentDetail(id ?? ''), [id]);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <p className="text-ink-muted">Reconstruindo o rastreio…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <Link to="/conteudo" className="text-xs underline">
          ← Voltar ao quadro
        </Link>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">
            {resource.httpStatus === 404
              ? 'Não encontrado, ou não visível para este acesso.'
              : 'Não foi possível carregar o rastreio.'}
          </p>
          {/*
            No 404 a mensagem da API repete o título palavra por palavra, e ler a
            mesma frase duas vezes faz a tela parecer quebrada. O texto útil ali é
            a explicação de por que as duas situações respondem igual.
          */}
          {resource.httpStatus === 404 ? null : <p className="mt-1">{resource.message}</p>}
          {resource.httpStatus === 404 ? (
            <p className="mt-2 text-xs text-ink-faint">
              As duas situações respondem igual de propósito: a tela não pode servir de oráculo
              sobre o que existe fora do seu alcance.
            </p>
          ) : null}
          <p className="mt-2 text-xs text-ink-faint">
            Código: {resource.code}
            {resource.correlationId ? ` · correlação: ${resource.correlationId}` : ''}
          </p>
        </div>
      </div>
    );
  }

  const c = resource.data;

  return (
    <div className="max-w-5xl space-y-4 text-sm">
      <Link to="/conteudo" className="text-xs underline">
        ← Voltar ao quadro
      </Link>

      <header className="mt-3">
        <h1 className="text-2xl font-bold">{c.topic}</h1>
        <p className="mt-1 text-ink-muted">
          Estado: <strong>{c.state}</strong> · formato {c.format} · objetivo {c.objective} · revisão{' '}
          {formatNumber(c.revision)}
        </p>
        {c.state_reason ? <p className="mt-1">{c.state_reason}</p> : null}
        {c.valid_until ? (
          <p className="mt-1 text-warn-ink">Validade do conteúdo: {formatInstant(c.valid_until)}</p>
        ) : null}
        <p className="mt-1 text-xs text-ink-faint">Correlação: {c.correlation_id}</p>

        {/*
          IMPULSIONAR (Paid Media SPEC §7; Console SPEC §242).

          O botão leva a peça já escolhida para o assistente de campanha. Ele NÃO
          decide se a peça pode ser impulsionada: quem decide é o Paid
          Distribution Gate, e o resultado dele aparece inteiro no detalhe do
          plano. Uma tela que escondesse o botão por conta própria estaria
          reimplementando a regra de elegibilidade, e a segunda cópia divergiria
          da primeira sem ninguém perceber.
        */}
        <p className="mt-3">
          <Link
            to={`/aquisicao?conteudo=${encodeURIComponent(c.content_id)}`}
            className="rounded border border-line-strong px-3 py-1 text-sm hover:bg-surface-raised"
          >
            Impulsionar
          </Link>
          <span className="ml-2 text-xs text-ink-muted">
            monta um plano com esta peça. Nada é gasto até alguém aprovar.
          </span>
        </p>
      </header>

      {/* ---- Por que existe ------------------------------------------------ */}
      <Section title="Por que esta peça existe">
        <Field label="Origem">{c.opportunity.source_type}</Field>
        <Field label="Oportunidade">{c.opportunity.why_it_matters}</Field>
        <Field label="Ângulo próprio">{c.opportunity.insight}</Field>
        <Field label="Urgência">{c.opportunity.timeliness}</Field>
        <Field label="Nota de oportunidade">
          {c.opportunity.score_opportunity === null ? (
            // Nota ausente NÃO vira zero: zero é a pior nota possível, ausente
            // é "ninguém calculou". Confundir os dois inverte a decisão.
            //
            // E o MOTIVO aparece junto. Sem ele, "não calculada" manda quem lê
            // procurar um defeito, quando a ação costuma ser aprovar os seis
            // pesos do Radar nos Controles.
            <span className="text-ink-faint">
              não calculada
              {c.opportunity.score_unavailable_reason
                ? `: ${c.opportunity.score_unavailable_reason}`
                : c.opportunity.score_weights_version
                  ? ` (pesos ${c.opportunity.score_weights_version})`
                  : ' (sem versão de pesos registrada)'}
            </span>
          ) : (
            <>
              {formatNumber(c.opportunity.score_opportunity)}
              {c.opportunity.score_weights_version ? (
                <span className="text-ink-faint">
                  {' '}
                  (pesos {c.opportunity.score_weights_version})
                </span>
              ) : null}
            </>
          )}
        </Field>
        {c.opportunity.expires_at ? (
          <Field label="Janela expira em">{formatInstant(c.opportunity.expires_at)}</Field>
        ) : null}
      </Section>

      {/* ---- Decisão editorial -------------------------------------------- */}
      <Section title="Decisão editorial">
        <Field label="Disposição">{c.editorial_decision.disposition}</Field>
        <Field label="Bucket do portfólio">
          {c.editorial_decision.portfolio_bucket ?? (
            <span className="text-ink-faint">não atribuído</span>
          )}
        </Field>
        <Field label="Prioridade">
          {c.editorial_decision.priority === null ? (
            <span className="text-ink-faint">não atribuída</span>
          ) : (
            formatNumber(c.editorial_decision.priority)
          )}
        </Field>
        <Field label="Motivo">{c.editorial_decision.reason}</Field>
        {c.editorial_decision.override_reason ? (
          <Field label="Override registrado">
            <span className="text-warn-ink">{c.editorial_decision.override_reason}</span>
          </Field>
        ) : null}
      </Section>

      {/* ---- Versões de roteiro ------------------------------------------- */}
      <Section title={`Versões de roteiro (${formatNumber(c.script_versions.length)})`}>
        {c.script_versions.length === 0 ? (
          <p className="text-ink-faint">Nenhuma versão de roteiro ainda.</p>
        ) : (
          <ul className="space-y-2">
            {c.script_versions.map((v) => (
              <li key={v.id} className="rounded border border-line p-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">Versão {formatNumber(v.version)}</span>
                  <span className="text-xs text-ink-muted">{v.state}</span>
                </div>
                {v.change_reason ? (
                  <p className="mt-1 text-xs">{v.change_reason}</p>
                ) : (
                  <p className="mt-1 text-xs text-ink-faint">Sem motivo de mudança registrado.</p>
                )}
                <p className="mt-1 text-xs text-ink-faint">
                  {v.first_person ? 'Primeira pessoa' : 'Não é primeira pessoa'} ·{' '}
                  {v.generated_with_ai ? 'gerado com IA' : 'sem IA'} · impressão semântica{' '}
                  {v.semantic_hash.slice(0, 12)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ---- Afirmações --------------------------------------------------- */}
      <Section title={`Afirmações materiais (${formatNumber(c.claims.length)})`}>
        {c.claims.length === 0 ? (
          <p className="text-ink-faint">
            Nenhuma afirmação extraída. Isso não é o mesmo que &quot;nenhuma afirmação existe&quot;:
            significa que a extração não registrou nada para esta peça.
          </p>
        ) : (
          <ul className="space-y-2">
            {c.claims.map((claim) => (
              <li key={claim.id} className="rounded border border-line p-2">
                <p>{claim.claim_text}</p>
                <p className="mt-1 text-xs text-ink-muted">
                  {claim.surface} · materialidade {claim.materiality} ·{' '}
                  <strong
                    className={
                      claim.verification_status === 'verified' ? 'text-ok-ink' : 'text-warn-ink'
                    }
                  >
                    {claim.verification_status}
                  </strong>
                  {claim.valid_until ? ` · vale até ${formatInstant(claim.valid_until)}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ---- Governança --------------------------------------------------- */}
      <Section title="Governança">
        {c.governance.bundles.length === 0 ? (
          <p className="text-ink-faint">Nenhum pacote de governança aberto.</p>
        ) : (
          c.governance.bundles.map((bundle) => {
            const reviews = c.governance.reviews.filter(
              (r) => r.script_version_id === bundle.script_version_id,
            );
            const recebidos = new Set(reviews.map((r) => r.reviewer));
            const faltando = bundle.required_reviews.filter((r) => !recebidos.has(r));
            return (
              <div key={bundle.id} className="mb-4 rounded border border-line p-3">
                <p className="font-medium">
                  Pacote {bundle.state} · tentativa {formatNumber(bundle.revision_attempt)}
                </p>
                <p className="mt-1 text-xs text-ink-muted">
                  Exigidos: {bundle.required_reviews.map((r) => REVIEWER_LABEL[r] ?? r).join(', ')}
                </p>

                {faltando.length > 0 ? (
                  <p className="mt-2 text-xs text-warn-ink">
                    Faltam responder: {faltando.map((r) => REVIEWER_LABEL[r] ?? r).join(', ')}.
                    Faltar um guardião nega o pacote: não existe maioria.
                  </p>
                ) : null}

                <ul className="mt-2 space-y-2">
                  {reviews.map((r) => (
                    <li key={r.id} className="border-t border-line pt-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-medium">
                          {REVIEWER_LABEL[r.reviewer] ?? r.reviewer}
                        </span>
                        <span className="text-xs">{r.result}</span>
                      </div>
                      <p className="mt-1 text-xs">{r.reason}</p>
                      <p className="mt-1 text-xs text-ink-faint">
                        política {r.policy_version}
                        {r.score === null ? '' : ` · nota ${formatNumber(r.score)}`}
                        {r.model_version ? ` · modelo ${r.model_version}` : ''}
                        {r.prompt_version ? ` · prompt ${r.prompt_version}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </Section>

      {/* ---- Portões ------------------------------------------------------ */}
      <Section title={`Portões avaliados (${formatNumber(c.gates.length)})`}>
        {c.gates.length === 0 ? (
          <p className="text-ink-faint">Nenhum portão avaliou esta peça ainda.</p>
        ) : (
          <ul className="space-y-2">
            {c.gates.map((g, i) => (
              <li
                key={`${g.gate_type}-${g.created_at}-${i}`}
                className="rounded border border-line p-2"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{GATE_LABEL[g.gate_type] ?? g.gate_type}</span>
                  <span className={`text-xs font-semibold ${OUTCOME_CLASS[g.result] ?? ''}`}>
                    {OUTCOME_LABEL[g.result] ?? g.result}
                  </span>
                </div>
                <p className="mt-1 text-xs">{g.reason}</p>
                <p className="mt-1 text-xs text-ink-faint">
                  {g.reason_code} · política {g.policy_version} · {formatInstant(g.created_at)}
                  {g.expires_at ? ` · vale até ${formatInstant(g.expires_at)}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ---- Decisões ----------------------------------------------------- */}
      <Section title={`Decisões registradas (${formatNumber(c.decisions.length)})`}>
        {c.decisions.length === 0 ? (
          <p className="text-ink-faint">Nenhuma decisão registrada nesta correlação.</p>
        ) : (
          <ul className="space-y-2">
            {c.decisions.map((d) => (
              <li key={d.id} className="rounded border border-line p-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{d.decision_type}</span>
                  <span className="text-xs text-ink-muted">{d.agent}</span>
                </div>
                <p className="mt-1 text-xs">{d.reason}</p>
                <p className="mt-1 text-xs text-ink-faint">
                  {formatInstant(d.occurred_at)}
                  {d.policy_refs.length > 0 ? ` · políticas ${d.policy_refs.join(', ')}` : ''}
                  {d.model_version ? ` · modelo ${d.model_version}` : ''}
                  {d.prompt_version ? ` · prompt ${d.prompt_version}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ---- Produção e publicação ---------------------------------------- */}
      <Section title="Produção e publicação">
        {c.production_assets.length === 0 ? (
          <p className="text-ink-faint">
            Nenhum ativo produzido. Produção paga só ocorre depois da aprovação semântica.
          </p>
        ) : (
          <ul className="space-y-2">
            {c.production_assets.map((a) => (
              <li key={a.id} className="rounded border border-line p-2">
                <p className="font-medium">
                  {a.type} v{formatNumber(a.version)} · {a.state}
                </p>
                <p className="mt-1 text-xs text-ink-muted">
                  legendas {a.subtitles_status} · áudio {a.audio_status} · QA visual{' '}
                  {a.visual_qa_status}
                </p>
              </li>
            ))}
          </ul>
        )}

        {c.publications.length > 0 ? (
          <ul className="mt-3 space-y-2">
            {c.publications.map((p) => (
              <li key={p.record_id} className="rounded border border-line p-2">
                <p className="font-medium">Publicação: {p.state}</p>
                <p className="mt-1 text-xs text-ink-muted">
                  {p.platform_post_id
                    ? `id na plataforma ${p.platform_post_id}`
                    : 'sem id na plataforma: nada foi confirmado como publicado'}
                  {p.published_at ? ` · ${formatInstant(p.published_at)}` : ''}
                  {p.retry_count > 0 ? ` · ${formatNumber(p.retry_count)} tentativa(s)` : ''}
                </p>
                {p.hold_reason_code ? (
                  <p className="mt-1 text-xs text-danger-ink">
                    Retido: {p.hold_reason_code}. Nenhuma nova tentativa ocorre até reconciliar com
                    a plataforma.
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </Section>

      {/* ---- Linha do tempo ----------------------------------------------- */}
      <Section title={`Linha do tempo (${formatNumber(c.transitions.length)} transições)`}>
        {c.transitions.length === 0 ? (
          <p className="text-ink-faint">Nenhuma transição registrada.</p>
        ) : (
          <ol className="space-y-2">
            {c.transitions.map((t, i) => (
              <li key={`${t.to}-${t.occurred_at}-${i}`} className="border-l-2 border-line pl-3">
                <p className="font-medium">
                  {t.from ?? '(criação)'} → {t.to}
                </p>
                <p className="mt-1 text-xs">{t.reason}</p>
                <p className="mt-1 text-xs text-ink-faint">
                  {t.reason_code} · {t.actor} · {formatInstant(t.occurred_at)}
                </p>
              </li>
            ))}
          </ol>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <Card>
        <h2 className="border-b border-line pb-1 text-xs font-semibold uppercase tracking-wider text-ink-muted">
          {title}
        </h2>
        <div className="mt-3">{children}</div>
      </Card>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <p className="mt-1">
      <span className="text-ink-muted">{label}:</span> {children}
    </p>
  );
}
